import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Box, LinearProgress } from "@mui/material";
import { useNavigate, useParams, Outlet, useSearchParams, useLocation } from "react-router-dom";
import IsMobile from "../../hooks/isMobile";
import useDebounce from "../../hooks/useDebounce";
import { ChatFilterState, IChat, IChatsRecord, IGetAllChatsParams } from "../../interfaces/chats";
import { ChatFilterTypeEnum } from "../../constants/enums";
import ChatsHeader from "../../components/pages-components/chat-page-components/ChatsHeader/ChatsHeader";
import ChatsList from "../../components/pages-components/chat-page-components/ChatsList/ChatsList";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { store } from "../../store";
import {
	convertToAppliedFilters,
	parseFiltersFromURL,
	getNewFilterValue,
	getTotalFiltersCount,
	buildChatsURLSearchParams,
	buildApiParamsHelper,
	createRoutePathForChatDetail,
} from "../../helpers/common";
import { isEmpty } from "lodash";
import { fetchTeamMemberSendbirdSessionThunk } from "../../store/user/userThunk";
import { DEFAULT_PAGE_LIMIT_MESSAGING, INITIAL_CHAT_FILTERS } from "../../constants/commonConstants";
import ChatsFilter from "../../components/pages-components/chat-page-components/ChatsFilter/ChatsFilter";
import FilterChips from "../../components/pages-components/chat-page-components/ChatsFilter/FilterChips/FilterChips";
import EmptyChatComponent from "../../components/pages-components/chat-page-components/ChatDetailHeader/EmptyChatComponent/EmptyChatComponent";
import useUserInfo from "../../hooks/reduxHelpersHooks/useUserInfo";
import { resetEventDetail } from "../../store/events/eventSlice";
import { setCorporateEventDetail } from "../../store/b2b/corporateEvent/corporateEventSlice";
import { useOnUpdateEventsInList } from "../../hooks/firestoreHooks/useOnUpdateEventsInList";
import {
	getAdminVendorChatRecordThunk,
	getAllChatsRecord,
	getChatRecordByEventId,
	getUnreadChannelsCount,
} from "../../store/chats/chatsThunk";
import { getEventValuesCMSThunk } from "../../store/events/eventThunk";
import { ROUTE_PATHS } from "../../routes";
import { REDUX_REQUEST_STATUS } from "../../types/commonTypes";
import {
	VendorMessagingSignal,
	useOnUpdateVendorMessagingSignals,
} from "../../hooks/firestoreHooks/vendorMessagingSignalsHook";
import {
	inboxCacheKey,
	moveInboxEvents,
	patchChatState,
	setSkipNextFirestoreUpdate,
	setSkipNextFirestoreUpdateDetail,
	settleInboxMoves,
	switchInboxView,
} from "../../store/chats/chatsSlice";
import { MESSAGING_ARCHIVED_VIEW } from "../../constants/chatConstants";
import ArchivedChatsHeader from "../../components/pages-components/chat-page-components/ChatsHeader/ArchivedChatsHeader";
import ArchivedRow from "../../components/pages-components/chat-page-components/ChatsList/ArchivedRow";
import { iChatMenuItem } from "../../components/pages-components/chat-page-components/ChatContextMenu/ChatContextMenu";
import useChatListActions, { iArchiveRequest } from "../../hooks/useChatListActions";
import {
	canMarkChatUnread,
	getEventChatTargets,
	getMarkUnreadChats,
	hasChatMessages,
	hasUnreadChat,
} from "../../utils/messaging/chatListState";
import LineInfoSvg from "../../assets/svg-fc/LineInfoSvg";
import LineArchiveSvg from "../../assets/svg-fc/LineArchiveSvg";
import LineMailSvg from "../../assets/svg-fc/LineMailSvg";
import LineMailOpenSvg from "../../assets/svg-fc/LineMailOpenSvg";
import LineUndoSvg from "../../assets/svg-fc/LineUndoSvg";
import "./chats.scss";

const withCachedListParams = <T extends IGetAllChatsParams>(params: T): T => {
	const { chatsRecord, allEvents } = store.getState().chats;
	return chatsRecord ? { ...params, limit: Math.max(allEvents.length, DEFAULT_PAGE_LIMIT_MESSAGING), silent: true } : params;
};

const ChatsListPage: React.FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { chatId, eventId: routeEventId } = useParams<{ chatId?: string; eventId?: string }>();
	const location = useLocation();
	const isMobile = IsMobile();
	const { userDetails } = useUserInfo();

	const initialSearch = searchParams.get("search") || "";
	const initialUnreadOnly = searchParams.get("isUnreadOnly") === "true";
	const isArchivedView = searchParams.get("view") === MESSAGING_ARCHIVED_VIEW;
	const urlArchivedSearch = searchParams.get("archivedSearch") || "";

	const [searchTerm, setSearchTerm] = useState(initialSearch);
	const [filterType, setFilterType] = useState<ChatFilterTypeEnum>(
		initialUnreadOnly ? ChatFilterTypeEnum.Unread : ChatFilterTypeEnum.All,
	);
	const [appliedFilters, setAppliedFilters] = useState<ChatFilterState>(parseFiltersFromURL(searchParams));
	const [expandedEvents, setExpandedEvents] = useState<Set<string>>(() => new Set(routeEventId ? [routeEventId] : []));
	const [archivedSearchTerm, setArchivedSearchTerm] = useState(urlArchivedSearch);
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [shouldStartUpdating, setShouldStartUpdating] = useState(false);

	// Abort getChatRecordByEventId only when the same eventId is requested again; different events can run in parallel.
	const chatByEventIdAbortControllerRef = useRef<AbortController | null>(null);
	const chatByEventIdEventIdRef = useRef<string | null>(null);
	const handleGetUnreadChannelCountRef = useRef<(fetchChatsRecord?: boolean) => void>(() => {});
	const prevArchivedViewRef = useRef<boolean | null>(null);

	const debouncedSearchTerm = useDebounce(searchTerm, 300);
	const debouncedArchivedSearchTerm = useDebounce(archivedSearchTerm, 300);
	const {
		chatsRecord,
		isLoading,
		latestRequestId,
		totalUnreadChannelsCount,
		adminVendorChatRecord,
		skipNextFirestoreUpdate,
		archivedSummary,
	} = useAppSelector((state) => state.chats);

	const events = chatsRecord?.events || [];
	const hasMore = chatsRecord?.pagination
		? chatsRecord.pagination.currentPage < chatsRecord.pagination.totalPages
		: false;
	const loadedPages = Math.floor(events.length / DEFAULT_PAGE_LIMIT_MESSAGING);

	const activeFiltersCount = useMemo(() => {
		return getTotalFiltersCount(appliedFilters);
	}, [appliedFilters]);

	const filtersResetKey = useMemo(
		() => JSON.stringify({ appliedFilters, filterType, search: debouncedSearchTerm.trim(), isArchivedView }),
		[appliedFilters, filterType, debouncedSearchTerm, isArchivedView],
	);

	const buildApiParams = useCallback(
		(page: number = 1, filters?: ChatFilterState, limit?: number): IGetAllChatsParams => {
			if (isArchivedView) {
				return {
					page,
					limit: limit ?? DEFAULT_PAGE_LIMIT_MESSAGING,
					search: urlArchivedSearch || undefined,
					archived: true,
				};
			}
			const filtersToUse = filters || appliedFilters;

			return buildApiParamsHelper(
				debouncedSearchTerm.trim() || "",
				filterType,
				filtersToUse,
				page,
				limit ?? DEFAULT_PAGE_LIMIT_MESSAGING,
			);
		},
		[debouncedSearchTerm, filterType, appliedFilters, isArchivedView, urlArchivedSearch],
	);

	const fetchInbox = useCallback(
		(apiParams: IGetAllChatsParams) => {
			const request = dispatch(getAllChatsRecord(apiParams));
			if (!apiParams.archived) {
				dispatch(getUnreadChannelsCount(apiParams));
			}
			return request;
		},
		[dispatch],
	);

	const handleAccordionToggle = (eventId: string) => {
		setExpandedEvents((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(eventId)) {
				newSet.delete(eventId);
			} else {
				newSet.add(eventId);
			}
			return newSet;
		});
	};

	const handleChatClick = (eventId: string, clickedChatId: string) => {
		dispatch(patchChatState({ chatIds: [clickedChatId], patch: { unreadMessageCount: 0, hasUnreadMention: false } }));
		if (routeEventId !== eventId) {
			dispatch(resetEventDetail());
			dispatch(setCorporateEventDetail(null));
		}
		const nextParams = new URLSearchParams(searchParams);
		nextParams.delete("details");
		const queryString = nextParams.toString();
		const newPath = queryString
			? `${createRoutePathForChatDetail(eventId, clickedChatId)}?${queryString}`
			: createRoutePathForChatDetail(eventId, clickedChatId);
		navigate(newPath);
	};

	const handleFilterApply = useCallback(
		(filters: ChatFilterState) => {
			setAppliedFilters(filters);

			const newSearchParams = buildChatsURLSearchParams(filters, debouncedSearchTerm, filterType);
			const queryString = newSearchParams.toString();
			const path = location.pathname;

			const newPath = queryString ? `${path}?${queryString}` : path;
			navigate(newPath, { replace: true });
		},
		[debouncedSearchTerm, filterType, navigate, location.pathname],
	);

	const handleRemoveFilter = useCallback(
		(filterKey: string, filterId?: string) => {
			handleFilterApply(getNewFilterValue(filterKey, appliedFilters, filterId));
		},
		[appliedFilters, handleFilterApply],
	);

	const handleClearAllFilters = useCallback(() => {
		handleFilterApply(INITIAL_CHAT_FILTERS);
	}, [handleFilterApply]);

	const handleLoadMore = useCallback(
		(page: number) => {
			fetchInbox(buildApiParams(page));
		},
		[fetchInbox, buildApiParams],
	);

	// Bootstrap CMS + Sendbird session once
	useEffect(() => {
		dispatch(getEventValuesCMSThunk({}));
		if (!isEmpty(userDetails?.sendbirdId)) {
			dispatch(fetchTeamMemberSendbirdSessionThunk());
		}
	}, [dispatch, userDetails?.sendbirdId]);

	// Single fetch when search/filter changes (includes initial mount)
	useEffect(() => {
		const params = buildApiParams(1);
		const isViewChange = prevArchivedViewRef.current !== isArchivedView;
		prevArchivedViewRef.current = isArchivedView;
		if (store.getState().chats.isArchivedInbox !== isArchivedView) {
			dispatch(switchInboxView({ archived: isArchivedView, key: inboxCacheKey(params), openEventId: routeEventId }));
		}
		fetchInbox(isViewChange ? withCachedListParams(params) : params).then((result) => {
			if (result.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
				setShouldStartUpdating(true);
			}
		});
	}, [buildApiParams]);

	useEffect(() => {
		if (!routeEventId) return;
		setExpandedEvents((prev) => {
			if (prev.has(routeEventId)) return prev;
			const next = new Set(prev);
			next.add(routeEventId);
			return next;
		});
	}, [routeEventId]);

	useEffect(() => {
		setExpandedEvents(new Set(routeEventId ? [routeEventId] : []));
	}, [isArchivedView]);

	useEffect(() => {
		if (!isArchivedView) return;
		const trimmed = debouncedArchivedSearchTerm.trim();
		if (trimmed === urlArchivedSearch) return;
		const nextParams = new URLSearchParams(searchParams);
		if (trimmed) nextParams.set("archivedSearch", trimmed);
		else nextParams.delete("archivedSearch");
		const queryString = nextParams.toString();
		navigate(`${location.pathname}${queryString ? `?${queryString}` : ""}`, { replace: true });
	}, [debouncedArchivedSearchTerm]);

	useEffect(() => {
		if (urlArchivedSearch === debouncedArchivedSearchTerm.trim()) return;
		setArchivedSearchTerm(urlArchivedSearch);
	}, [urlArchivedSearch]);

	// Sync search term and filter state with URL query parameters
	useEffect(() => {
		if (isArchivedView) return;
		const newSearchParams = new URLSearchParams(searchParams);
		if (debouncedSearchTerm.trim()) {
			newSearchParams.set("search", debouncedSearchTerm.trim());
		} else {
			newSearchParams.delete("search");
		}

		const isUnreadOnly = filterType === ChatFilterTypeEnum.Unread;
		if (isUnreadOnly) {
			newSearchParams.set("isUnreadOnly", "true");
		} else {
			newSearchParams.delete("isUnreadOnly");
		}

		const currentSearch = searchParams.get("search") || "";
		const currentUnreadOnly = searchParams.get("isUnreadOnly") === "true";
		const newSearch = debouncedSearchTerm.trim();
		const newUnreadOnly = isUnreadOnly;

		if (currentSearch !== newSearch || currentUnreadOnly !== newUnreadOnly) {
			const queryString = newSearchParams.toString();
			const newPath = queryString ? `?${queryString}` : "";
			navigate(location.pathname + newPath, { replace: true });
		}
	}, [debouncedSearchTerm, filterType, searchParams, navigate, location.pathname, isArchivedView]);

	handleGetUnreadChannelCountRef.current = (fetchChatsRecord: boolean = false) => {
		const limit = Math.max(events.length, DEFAULT_PAGE_LIMIT_MESSAGING);
		const apiParams = buildApiParams(1, undefined, limit);
		if (!apiParams.archived) {
			dispatch(getUnreadChannelsCount({ ...apiParams, silent: true }));
		}
		if (fetchChatsRecord) {
			dispatch(getAllChatsRecord({ ...apiParams, silent: true }));
		}
	};

	const handleGetUnreadChannelCount = useCallback((fetchChatsRecord: boolean = false) => {
		handleGetUnreadChannelCountRef.current(fetchChatsRecord);
	}, []);

	const hasFilters = useMemo(() => {
		const converToAppliedFilters = convertToAppliedFilters(appliedFilters);
		return (
			converToAppliedFilters.orderId ||
			converToAppliedFilters.eventDateStart ||
			converToAppliedFilters.createdDateStart ||
			(converToAppliedFilters.vendorEventStatus && converToAppliedFilters.vendorEventStatus.length > 0) ||
			converToAppliedFilters.eventType
		);
	}, [appliedFilters]);

	const emptyChatHeading = useMemo(() => {
		return (hasFilters || searchTerm.trim() !== "" || initialUnreadOnly) && chatsRecord?.events?.length === 0
			? "No messages"
			: null;
	}, [hasFilters, searchTerm, chatsRecord, initialUnreadOnly]);

	async function onEventModified(eventId: string) {
		if (skipNextFirestoreUpdate) {
			dispatch(setSkipNextFirestoreUpdate(false));
			return;
		}
		const existsInCurrentList = events.some((event) => event.eventId === eventId);
		if (existsInCurrentList) {
			// Abort previous request only if it was for the same eventId; different events can run in parallel.
			if (chatByEventIdEventIdRef.current === eventId && chatByEventIdAbortControllerRef.current) {
				chatByEventIdAbortControllerRef.current.abort();
				chatByEventIdAbortControllerRef.current = null;
			}
			chatByEventIdEventIdRef.current = eventId;
			const controller = new AbortController();
			chatByEventIdAbortControllerRef.current = controller;

			const listedChatsOnly = filterType === ChatFilterTypeEnum.Unread || !!debouncedSearchTerm.trim() || !!hasFilters;
			const req = await dispatch(getChatRecordByEventId({ eventId, signal: controller.signal, listedChatsOnly }));
			if (req.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED && !isArchivedView) {
				dispatch(getUnreadChannelsCount({ ...buildApiParams(1), silent: true }));
			}
		}
	}

	const refreshMessagingInbox = useCallback(() => {
		const limit = Math.max(events.length, DEFAULT_PAGE_LIMIT_MESSAGING);
		return fetchInbox({ ...buildApiParams(1, undefined, limit), silent: true });
	}, [fetchInbox, buildApiParams, events.length]);

	const setArchivedView = useCallback(
		(archived: boolean) => {
			const nextParams = new URLSearchParams(searchParams);
			nextParams.delete("archivedSearch");
			if (archived) nextParams.set("view", MESSAGING_ARCHIVED_VIEW);
			else nextParams.delete("view");
			setArchivedSearchTerm("");
			const queryString = nextParams.toString();
			navigate(`${location.pathname}${queryString ? `?${queryString}` : ""}`);
		},
		[searchParams, navigate, location.pathname],
	);

	const navigateToList = useCallback(() => {
		const queryString = searchParams.toString();
		navigate(`${ROUTE_PATHS.CHATS}${queryString ? `?${queryString}` : ""}`);
	}, [searchParams, navigate]);

	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const handleArchiveChange = useCallback(
		(request: iArchiveRequest, archived: boolean) => {
			dispatch(moveInboxEvents({ eventIds: [request.eventId], toArchived: archived }));
		},
		[dispatch],
	);

	const handleArchiveSettled = useCallback(
		(request: iArchiveRequest, archived: boolean, succeeded: boolean) => {
			const eventIds = [request.eventId];
			const settle = () => dispatch(settleInboxMoves({ eventIds, toArchived: archived }));
			if (!succeeded) dispatch(settleInboxMoves({ eventIds, toArchived: archived, revert: true }));
			if (!isMountedRef.current) {
				settle();
				return;
			}
			void refreshMessagingInbox().finally(settle);
		},
		[dispatch, refreshMessagingInbox],
	);

	const refreshMountedInbox = useCallback(() => {
		if (!isMountedRef.current) return;
		void refreshMessagingInbox();
		void dispatch(getAdminVendorChatRecordThunk());
	}, [dispatch, refreshMessagingInbox]);

	const refreshAfterReadChange = useCallback(() => {
		if (!isMountedRef.current) return;
		if (isArchivedView || filterType === ChatFilterTypeEnum.Unread) {
			void refreshMessagingInbox();
			return;
		}
		dispatch(getUnreadChannelsCount({ ...buildApiParams(1), silent: true }));
	}, [buildApiParams, dispatch, filterType, isArchivedView, refreshMessagingInbox]);

	const chatActions = useChatListActions({
		onArchiveChange: handleArchiveChange,
		onArchiveSettled: handleArchiveSettled,
		onChatStateChange: refreshMountedInbox,
		onReadStateChange: refreshAfterReadChange,
	});
	const { isOwnChatStateSignal } = chatActions;

	const handleMarkUnread = useCallback(
		(chats: IChat[]) => {
			if (chatId && chats.some((chat) => chat.id === chatId)) {
				navigateToList();
			}
			void chatActions.markUnread(chats);
		},
		[chatActions, chatId, navigateToList],
	);

	const getDetailsChat = useCallback(
		(event: IChatsRecord) =>
			event.chats.find((chat) => chat.id === chatId) ?? event.chats.find((chat) => Boolean(chat.channelUrl)),
		[chatId],
	);

	const handleViewEventDetails = useCallback(
		(event: IChatsRecord) => {
			const targetChat = getDetailsChat(event);
			if (!targetChat) return;
			if (routeEventId !== event.eventId) {
				dispatch(resetEventDetail());
				dispatch(setCorporateEventDetail(null));
			}
			const nextParams = new URLSearchParams(searchParams);
			nextParams.set("details", "open");
			navigate(`${createRoutePathForChatDetail(event.eventId, targetChat.id)}?${nextParams.toString()}`);
		},
		[getDetailsChat, routeEventId, searchParams, navigate, dispatch],
	);

	const readStateItem = useCallback(
		(chats: IChat[]): iChatMenuItem | null => {
			const readStateChats = getMarkUnreadChats(chats);
			const unreadChats = readStateChats.filter(hasUnreadChat);
			if (unreadChats.length) {
				return {
					key: "read",
					label: "Mark as read",
					icon: <LineMailOpenSvg />,
					onClick: () => void chatActions.markRead(unreadChats),
				};
			}
			const markableChats = readStateChats.filter(hasChatMessages);
			if (markableChats.length) {
				return {
					key: "unread",
					label: "Mark as unread",
					icon: <LineMailSvg />,
					onClick: () => handleMarkUnread(markableChats),
				};
			}
			return null;
		},
		[chatActions, handleMarkUnread],
	);

	const getEventMenuItems = useCallback(
		(event: IChatsRecord): iChatMenuItem[] => {
			const archiveRequest: iArchiveRequest = {
				eventId: event.eventId,
				targets: getEventChatTargets(event),
				message: event.eventName,
				isEvent: true,
			};
			const items: iChatMenuItem[] = [];
			if (getDetailsChat(event)) {
				items.push({
					key: "details",
					label: "View event details",
					icon: <LineInfoSvg />,
					onClick: () => handleViewEventDetails(event),
				});
			}
			if (isArchivedView) {
				items.push({
					key: "unarchive",
					label: "Unarchive",
					icon: <LineUndoSvg />,
					onClick: () => void chatActions.unarchive(archiveRequest),
				});
				return items;
			}
			items.push({
				key: "archive",
				label: "Archive chats",
				icon: <LineArchiveSvg />,
				onClick: () => void chatActions.archive(archiveRequest),
			});
			const readItem = readStateItem(event.chats);
			if (readItem) items.push(readItem);
			return items;
		},
		[isArchivedView, chatActions, handleViewEventDetails, getDetailsChat, readStateItem],
	);

	const getChatMenuItems = useCallback(
		(_event: IChatsRecord, chat: IChat): iChatMenuItem[] => {
			const readItem = readStateItem([chat]);
			return readItem ? [readItem] : [];
		},
		[readStateItem],
	);

	const isSupportPage = location.pathname === ROUTE_PATHS.ADMIN_VENDOR_CHAT;

	const getSupportMenuItems = useCallback((): iChatMenuItem[] => {
		if (!adminVendorChatRecord) return [];
		const items: iChatMenuItem[] = [];
		const supportChat = {
			id: adminVendorChatRecord.id,
			channelUrl: adminVendorChatRecord.supportChannelUrl,
			isNewModelChannel: adminVendorChatRecord.isSupportSendbirdNewModel === true,
			unreadMessageCount: adminVendorChatRecord.unreadMessageCount,
			hasUnreadMention: adminVendorChatRecord.hasUnreadMention,
		};
		if (canMarkChatUnread(supportChat) && (hasUnreadChat(supportChat) || hasChatMessages(adminVendorChatRecord))) {
			items.push(
				hasUnreadChat(supportChat)
					? {
							key: "read",
							label: "Mark as read",
							icon: <LineMailOpenSvg />,
							onClick: () => void chatActions.markRead([supportChat]),
						}
					: {
							key: "unread",
							label: "Mark as unread",
							icon: <LineMailSvg />,
							onClick: () => {
								if (isSupportPage) navigateToList();
								void chatActions.markUnread([supportChat]).then((marked) => {
									if (marked) void dispatch(getAdminVendorChatRecordThunk());
								});
							},
						},
			);
		}
		return items;
	}, [adminVendorChatRecord, chatActions, isSupportPage, navigateToList, dispatch]);

	const refreshChatsFromFirestore = useCallback(
		(signal: VendorMessagingSignal) => {
			if (signal.chatStateOnly && isOwnChatStateSignal(signal.chatStateUpdatedAt)) {
				return;
			}
			void refreshMessagingInbox();
			if (signal.chatStateUpdatedAt !== null) void dispatch(getAdminVendorChatRecordThunk());
		},
		[dispatch, isOwnChatStateSignal, refreshMessagingInbox],
	);

	useOnUpdateVendorMessagingSignals(shouldStartUpdating, refreshChatsFromFirestore);
	useOnUpdateEventsInList(shouldStartUpdating, events, onEventModified);

	// Abort getChatRecordByEventId on unmount
	useEffect(() => {
		return () => {
			if (chatByEventIdAbortControllerRef.current) {
				chatByEventIdAbortControllerRef.current.abort();
				chatByEventIdAbortControllerRef.current = null;
			}
		};
	}, []);

	const archivedUnreadCount = archivedSummary?.unreadChatCount ?? 0;
	const shouldShowEmptyChat = isSupportPage ? !adminVendorChatRecord?.id : !chatId;

	// On mobile, if chatId exists, don't render the list (ChatDetailPage will render instead)
	if (isMobile && (isSupportPage ? adminVendorChatRecord : chatId)) {
		return <Outlet />;
	}

	return (
		<>
			{isLoading && chatsRecord && latestRequestId !== null && (
				<Box className="chatsLoadingBarChatsListPage">
					<LinearProgress className="chatsLinearProgress" />
				</Box>
			)}
			<Box className="content chat_tab chatsPageContainer">
				<Box className={`chatsListContainer ${isMobile ? "mobile" : "desktop"}`}>
					{isArchivedView ? (
						<ArchivedChatsHeader
							searchTerm={archivedSearchTerm}
							onSearchChange={setArchivedSearchTerm}
							onBack={() => setArchivedView(false)}
						/>
					) : (
						<>
							<ChatsHeader
								searchTerm={searchTerm}
								onSearchChange={setSearchTerm}
								filterType={filterType}
								onFilterChange={setFilterType}
								onFilterClick={() => setIsFilterOpen(true)}
								activeFiltersCount={activeFiltersCount}
								totalUnreadChannelsCount={totalUnreadChannelsCount}
							/>
							<FilterChips
								filters={convertToAppliedFilters(appliedFilters)}
								onRemoveFilter={handleRemoveFilter}
								onClearAll={handleClearAllFilters}
								numberOfEvents={events.length}
								hasFilters={hasFilters || false}
							/>
						</>
					)}
					<ChatsList
						archived={isArchivedView}
						getEventMenuItems={getEventMenuItems}
						getChatMenuItems={getChatMenuItems}
						getSupportMenuItems={getSupportMenuItems}
						archivedRow={
							isArchivedView ? null : (
								<ArchivedRow unreadChatCount={archivedUnreadCount} onClick={() => setArchivedView(true)} />
							)
						}
						chatsRecord={chatsRecord}
						expandedEvents={expandedEvents}
						onAccordionToggle={handleAccordionToggle}
						selectedChatId={chatId}
						onChatClick={handleChatClick}
						loading={isLoading}
						hasMore={hasMore}
						currentPage={loadedPages}
						onLoadMore={handleLoadMore}
						filtersResetKey={filtersResetKey}
						handleGetUnreadChannelCount={handleGetUnreadChannelCount}
						onSendbirdHandledUpdate={() => {
							dispatch(setSkipNextFirestoreUpdate(true));
							dispatch(setSkipNextFirestoreUpdateDetail(true));
						}}
					/>
					<ChatsFilter
						open={isFilterOpen}
						onClose={() => setIsFilterOpen(false)}
						onApply={handleFilterApply}
						initialFilters={appliedFilters}
					/>
				</Box>
				{!isMobile && (
					<Box className="chatDetailContainer">
						{shouldShowEmptyChat ? <EmptyChatComponent emptyChatHeading={emptyChatHeading} /> : <Outlet />}
						{isFilterOpen && <Box className="chatsFilterBackdrop" onClick={() => setIsFilterOpen(false)} />}
					</Box>
				)}
			</Box>
		</>
	);
};

export default ChatsListPage;
